<template>
  <div>
    <b-sidebar
      id="viewShift"
      bg-variant="white"
      right
      backdrop
      shadow
      width="520px"
      class="sidebar-form"
      @hidden="sidebarToggle"
    >
      <h3 class="form-title">
        View Shift
      </h3>
      <div class="line user-underline" />

      <div v-if="shift">
        <!-- Basic Information -->
        <div class="info-section">
          <p class="section-title">
            Basic Information
          </p>
          <div class="info-row">
            <span class="info-label">Shift Name</span>
            <span class="info-value">{{ shift.name || '-' }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Shift Code</span>
            <span class="info-value">{{ shift.code || '-' }}</span>
          </div>
        </div>

        <!-- Shift Timing -->
        <div class="info-section">
          <p class="section-title">
            Shift Timing
          </p>
          <div class="info-row">
            <span class="info-label">Start Time</span>
            <span class="info-value">{{ shift.start_time | fullTimeFormat }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">End Time</span>
            <span class="info-value">{{ shift.end_time | fullTimeFormat }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Lunch Break</span>
            <span class="info-value">{{ shift.lunch_break_minutes != null ? shift.lunch_break_minutes + ' min' : '-' }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Working Hours</span>
            <span class="info-value">{{ formatDecimalHours(shift.total_working_hours) }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Min. Full Day Hours</span>
            <span class="info-value">{{ formatDecimalHours(shift.minimum_full_day_hours) }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Minimum hours required per day</span>
            <span class="info-value">{{ formatDecimalHours(shift.grace_period_start_hours) }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Half Day Hours</span>
            <span class="info-value">{{ formatDecimalHours(shift.half_day_hours) }}</span>
          </div>
        </div>

        <!-- Weekly Configuration -->
        <div class="info-section">
          <p class="section-title">
            Weekly Configuration
          </p>

          <!-- Working Days -->
          <div class="info-col">
            <span class="info-label mb-50 d-block">Working Days</span>
            <weekday-selector
              v-if="shift.working_days && shift.working_days.length"
              :value="shift.working_days"
              sm
              active-color="#2178fb"
              shadow-color="rgba(33,120,251,0.3)"
              readonly
            />
            <span v-else class="text-muted">-</span>
          </div>

          <!-- Weekly Off Days -->
          <div class="info-col mt-1">
            <span class="info-label mb-50 d-block">Weekly Off Days</span>
            <weekday-selector
              v-if="shift.weekly_off_days && shift.weekly_off_days.length"
              :value="shift.weekly_off_days"
              sm
              active-color="#ff9f43"
              shadow-color="rgba(255,159,67,0.3)"
              readonly
            />
            <span v-else class="text-muted">-</span>
          </div>
        </div>

        <!-- Grace Period -->
        <div class="info-section">
          <p class="section-title">
            Grace Period
          </p>
          <div class="info-row">
            <span class="info-label">Grace Minutes</span>
            <span class="info-value">{{ shift.grace_minutes != null ? shift.grace_minutes + ' min' : '-' }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Maximum days allowed in a week</span>
            <span class="info-value">{{ shift.grace_days_allowed != null ? shift.grace_days_allowed + ' days' : '-' }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Lunch Break</span>
            <span class="info-value">{{ shift.lunch_break_minutes != null ? shift.lunch_break_minutes + ' min' : '-' }}</span>
          </div>
        </div>

        <!-- Status & Metadata -->
        <div class="info-section">
          <p class="section-title">
            Other Details
          </p>
          <div class="info-row">
            <span class="info-label">Status</span>
            <span class="info-value">
              <b-badge :variant="isActive ? 'light-success' : 'light-danger'">
                {{ isActive ? 'Active' : 'Inactive' }}
              </b-badge>
            </span>
          </div>
          <div class="info-row">
            <span class="info-label">Created By</span>
            <div class="info-value d-flex align-items-center justify-content-end">
              <template v-if="shift.created_by">
                <b-avatar
                  size="26"
                  :text="shift.created_by.full_name | avatarText"
                  :src="shift.created_by.picture || null"
                  style="background-color: #f97753"
                  class="mr-50"
                />
                <span>{{ shift.created_by.full_name }}</span>
              </template>
              <span v-else>-</span>
            </div>
          </div>
        </div>
      </div>

      <template #footer>
        <div class="form-button d-flex my-2">
          <button
            class="btn side-bar-btn"
            @click="editShift"
          >
            Edit
          </button>
          <button
            class="btn side-bar-btn"
            @click="closeView"
          >
            Close
          </button>
        </div>
      </template>
    </b-sidebar>
  </div>
</template>

<script>
import ViewShiftMixin from '@/mixin/masterSetting/Shift/ViewShift'

export default {
  mixins: [ViewShiftMixin],
}
</script>

<style scoped lang="scss">
.info-section {
  margin-bottom: 1.5rem;
}

.section-title {
  font-size: 13px;
  font-weight: 600;
  color: #5e5873;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid #ebe9f1;
  padding-bottom: 6px;
  margin-bottom: 1rem;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.6rem 0;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }
}

/* Stack layout for day selectors (full width, not side-by-side) */
.info-col {
  padding: 0.5rem 0;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }
}

.info-label {
  font-weight: 600;
  font-size: 14px;
  color: #5e5873;
  flex: 0 0 50%;
}

.info-value {
  font-size: 14px;
  color: #6e6b7b;
  flex: 1;
  text-align: right;
}
</style>
